import { memo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import { StopCircle } from 'lucide-react'
import type { Message } from '@/types/conversations'
import { RelativeTime } from '@/components/ui/relative-time'
import { useStreamingAnimation } from '@/hooks/useStreamingAnimation'
import { removeControlTags } from '@/lib/remove-control-tags'

interface AssistantMessageProps {
  message: Message
  isStreaming?: boolean
  showTimestamp?: boolean
  isOrphaned?: boolean
}

/**
 * Find the last safe paragraph boundary (\n\n) that's not inside a code fence.
 * Returns the index right after the \n\n, or -1 if no safe break found.
 */
function findLastSafeBreak(content: string): number {
  let inCodeFence = false
  let lastBreak = -1
  let i = 0

  while (i < content.length) {
    // Check for code fence (``` or ~~~)
    if (
      (content[i] === '`' && content[i + 1] === '`' && content[i + 2] === '`') ||
      (content[i] === '~' && content[i + 1] === '~' && content[i + 2] === '~')
    ) {
      // Only toggle on fences at start of line
      if (i === 0 || content[i - 1] === '\n') {
        inCodeFence = !inCodeFence
      }
      // Skip past the fence markers
      i += 3
      while (i < content.length && content[i] !== '\n') i++
      continue
    }

    // Check for paragraph break (\n\n) outside code fences
    if (!inCodeFence && content[i] === '\n' && content[i + 1] === '\n') {
      lastBreak = i + 2
      i += 2
      continue
    }

    i++
  }

  return lastBreak
}

const REMARK_PLUGINS = [remarkGfm]
const REHYPE_PLUGINS_FULL = [rehypeHighlight]

/**
 * Streaming markdown renderer: memoizes completed paragraphs, only re-renders the tail.
 * Completed paragraphs get full ReactMarkdown + rehypeHighlight.
 * The streaming tail gets ReactMarkdown only (no syntax highlighting — code blocks are likely incomplete).
 */
function StreamingMarkdown({ content }: { content: string }) {
  const prevStableRef = useRef('')
  const stableRenderedRef = useRef<React.ReactNode>(null)

  const breakIdx = findLastSafeBreak(content)
  const stableContent = breakIdx > 0 ? content.slice(0, breakIdx) : ''
  const tailContent = breakIdx > 0 ? content.slice(breakIdx) : content

  // Only re-render stable portion when it actually changes (new paragraph completed)
  if (stableContent && stableContent !== prevStableRef.current) {
    prevStableRef.current = stableContent
    stableRenderedRef.current = (
      <ReactMarkdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS_FULL}>
        {stableContent}
      </ReactMarkdown>
    )
  } else if (!stableContent) {
    prevStableRef.current = ''
    stableRenderedRef.current = null
  }

  return (
    <>
      {stableRenderedRef.current}
      {tailContent && (
        <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>
          {tailContent}
        </ReactMarkdown>
      )}
    </>
  )
}

export const AssistantMessage = memo(function AssistantMessage({ message, isStreaming = false, showTimestamp = true, isOrphaned = false }: AssistantMessageProps) {
  const { t } = useTranslation('conversations')
  const isInterrupted = message.status === 'interrupted'

  const cleanContent = removeControlTags(message.content)
  const displayedContent = useStreamingAnimation(cleanContent, isStreaming)

  return (
    <div className={`flex gap-3 animate-in fade-in slide-in-from-left-2 duration-300 min-w-0 mt-4 ${isOrphaned ? 'opacity-50' : ''}`}>
      <div className="flex flex-col gap-1 min-w-0 flex-1 max-w-full">
        <div className="prose prose-sm dark:prose-invert max-w-none text-sm [overflow-wrap:anywhere]">
          {isStreaming ? (
            <StreamingMarkdown content={displayedContent} />
          ) : (
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS_FULL}>
              {displayedContent}
            </ReactMarkdown>
          )}
        </div>
        {isInterrupted && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
            <StopCircle className="h-3 w-3" />
            <span>{t('chat.interrupted')}</span>
          </div>
        )}
        {!isStreaming && !isInterrupted && showTimestamp && (
          <RelativeTime
            timestamp={message.created_at}
            className="text-xs text-muted-foreground"
          />
        )}
      </div>
    </div>
  )
})
